/**
 * PasteImport — the bulk-entry mechanic (§26). A paste panel that takes a block
 * from an external source (e.g. a directional service's survey stations, one
 * `MD Inc Azi` per line) and splits each line into grid rows on "Parse &
 * append". Whitespace | tab | comma separated. Computed columns (TVD / N-S /
 * E-W / DLS) are left as server-filled placeholders — the FE never computes
 * them (min-curvature lives in the OXBO-x compute-server, §15).
 *
 * The generic hook mirrors the wireframe's `[data-paste-parse]` +
 * `data-parse-cols` / `data-parse-extra` contract, expressed as props here so
 * screens configure it declaratively. `parsePasteBlock` is exported pure for
 * unit testing.
 */
import { useCallback, useState } from "react";
import { parsePasteBlock, type ParsedRow } from "./parsePaste";

export type { ParsedRow, ParseResult } from "./parsePaste";

export interface PasteImportProps {
  /** Number of input columns expected per line (e.g. 3 for MD/Inc/Azi). */
  cols: number;
  /** Called with parsed rows on "Parse & append". */
  onParse: (rows: ParsedRow[]) => void;
  /** Panel title. */
  title?: string;
  /** Format hint shown under the title (e.g. "MD  Inc  Azi"). */
  hint?: string;
  /** Names of the computed columns the server fills (shown in the note). */
  computedNote?: string;
  /** Textarea placeholder. */
  placeholder?: string;
  /** Report closed → disable. */
  readOnly?: boolean;
  /** Button label. */
  actionLabel?: string;
}

export function PasteImport({
  cols,
  onParse,
  title = "Paste import",
  hint,
  computedNote,
  placeholder,
  readOnly = false,
  actionLabel = "Parse & append",
}: PasteImportProps) {
  const [text, setText] = useState("");
  const [note, setNote] = useState<{ msg: string; tone: "ok" | "warn" } | null>(null);

  const handleParse = useCallback(() => {
    const { rows, skipped } = parsePasteBlock(text, cols);
    if (rows.length === 0) {
      setNote({ msg: "No rows parsed — check the format.", tone: "warn" });
      return;
    }
    onParse(rows);
    setText(""); // clear the textarea after a successful append
    const skip = skipped > 0 ? ` (${skipped} line${skipped === 1 ? "" : "s"} skipped)` : "";
    setNote({
      msg: `Parsed ${rows.length} row${rows.length === 1 ? "" : "s"}${skip}.`,
      tone: skipped > 0 ? "warn" : "ok",
    });
  }, [text, cols, onParse]);

  return (
    <div className="paste-card">
      <h3>
        <span className="ic ic-import" aria-hidden="true" />
        {title}
      </h3>
      {hint ? (
        <p className="hint">
          One record per line, whitespace / tab / comma separated: <code>{hint}</code>.
          {computedNote ? ` ${computedNote}` : ""}
        </p>
      ) : null}
      <div className="paste-row">
        <textarea
          aria-label={title}
          value={text}
          placeholder={placeholder}
          disabled={readOnly}
          onChange={(e) => setText(e.target.value)}
        />
        <div>
          <button
            type="button"
            className="btn-inline primary"
            data-paste-parse
            data-parse-cols={cols}
            disabled={readOnly || text.trim().length === 0}
            onClick={handleParse}
          >
            <span className="ic ic-add" aria-hidden="true" />
            {actionLabel}
          </button>
        </div>
      </div>
      {note ? (
        <p className={`parse-note ${note.tone}`} role="status">
          {note.msg}
        </p>
      ) : (
        <p className="parse-note" aria-hidden="true" />
      )}
    </div>
  );
}
